import { GroupBox } from 'app/components';
import { DataStoreSelectors, EntityType } from 'app/dataStore';
import { NCustomAsset, NProgram } from 'app/dataStore/types';
import { fetchCustomAssets, saveCustomAssets } from 'app/newnity/duck/thunks';
import i18n from 'localization/i18n';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    BladeActions, ColumnSettings, DataGrid, DataGridSelectionType, openBlade,
    SelectionBladeContainerProps, SelectionListBladeType
} from 'react-tools';

import { ValueFormatterParams } from '@ag-grid-enterprise/all-modules';
import { Button, CircularProgress } from '@material-ui/core';
import Edit from '@material-ui/icons/Edit';

import {
    getDataStoreCollection, getDataStoreCollectionStatus
} from '../../../../dataStore/selectors/genericSelectors';
import { useStyles } from './zoneList.jss';

export interface CustomAssetsProps {
  workgroupId: number;
  parentBladeId: string;
}

const gridColumns = (bladeGrid: boolean): ColumnSettings[] => [
  {
    name: 'id',
    width: 80,
    hide: true,
  },
  {
    name: 'name',
    width: 320,
    lockVisibility: true,
    filter: 'agTextColumnFilter',
    sort: { direction: 'asc', order: 1 },
  },
  {
    name: 'description',
    width: 500,
    hide: !bladeGrid,
    filter: 'agTextColumnFilter',
  },
  {
    name: 'externalId',
    width: 120,
    filter: 'agTextColumnFilter',
  },
  {
    name: 'genre',
    width: 120,
    hide: true,
    filter: 'agTextColumnFilter',
  },
  {
    name: 'ratingLevel',
    width: 120,
    hide: true,
    filter: 'agTextColumnFilter',
  },
];

export const CustomAssets: React.FC<CustomAssetsProps> = (props) => {
  const { workgroupId, parentBladeId } = props;
  const classes = useStyles();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const companyCustomAssetsCatalog = useSelector((state) =>
    DataStoreSelectors.NProgram.selectCustomProgramItems(state)
  );
  const companyCustomAssetsCatalogFetchStatus = useSelector((state) =>
    DataStoreSelectors.getDataStoreItemsFetching(state, EntityType.NProgram)
  );
  const companyInfo = useSelector((state) =>
    DataStoreSelectors.NCompany.selectCompanyById(state, workgroupId)
  );

  const [templateSelectionBladeOpen, setTemplateSelectionBladeOpen] = useState(false);

  const openTemplateLibrarySelection = useCallback(() => {
    if (!templateSelectionBladeOpen) {
      dispatch(fetchCustomAssets(workgroupId, companyInfo.licensingEntityId));

      dispatch(
        BladeActions.closeChildrenBlades(
          parentBladeId,
          openBlade<SelectionBladeContainerProps>(parentBladeId, SelectionListBladeType, {
            columns: gridColumns(true),
            identifier: 'customAssets',
            onClose: (items: NCustomAsset[] | null) => {
              if (items !== null) {
                const addedItems = items.filter(
                  (x) => companyCustomAssetsCatalog.findIndex((y) => x.id === y.id) === -1
                );
                const removedItems = companyCustomAssetsCatalog.filter(
                  (x) => items.findIndex((y) => x.id === y.id) === -1
                );
                console.log('Added items: ' + JSON.stringify(addedItems));
                console.log('Removed items: ' + JSON.stringify(removedItems));
                dispatch(saveCustomAssets(workgroupId, addedItems, removedItems));
              }
              setTemplateSelectionBladeOpen(false);
            },
            selectionType: DataGridSelectionType.Multiple,
            title: t('newnity.music.customPrograms.title'),
            itemsSelector: getDataStoreCollection(EntityType.NCustomAsset),
            selectedItems: companyCustomAssetsCatalog,
            isFetchingSelector: getDataStoreCollectionStatus(EntityType.NCustomAsset),
            submitButtonLabel: t('templateLibraries.submitButtonLabel'),
          })
        )
      );
      setTemplateSelectionBladeOpen(true);
    }
  }, [companyCustomAssetsCatalog, workgroupId, companyInfo, parentBladeId]);

  return (
    <GroupBox
      grow
      title={t('newnity.music.customPrograms.title')}
      headerContent={
        companyCustomAssetsCatalogFetchStatus.isFetching ? (
          <CircularProgress size={32} />
        ) : (
          <Button
            size="small"
            variant="contained"
            startIcon={<Edit />}
            onClick={openTemplateLibrarySelection}
          >
            {t('edit')}
          </Button>
        )
      }
    >
      <DataGrid
        identifier={'customAssets'}
        idColumn={'id'}
        rowClass={classes.alignedRow}
        items={companyCustomAssetsCatalog}
        selectionType={DataGridSelectionType.None}
        columnSettings={gridColumns(false)}
        externalFilterFn={undefined}
        isExternalFilterEnabled={undefined}
        noDataMessage="customAssets.noData"
      />
    </GroupBox>
  );
};
